// src/modules/app-views/customers/pages/Init/modals/CustomersParametersModal/tabs/BusinessAreaTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import {
  Card,
  Flex,
  Typography,
  Button,
  Empty,
  Popconfirm,
  Badge,
  ColorPicker,
  Input,
  Form,
  Modal,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TagOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateMongoObjectId } from 'utils/helpers';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEFAULT_FORE_COLOR = '#ffffff';
const DEFAULT_BACK_COLOR = '#1890ff';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const BusinessAreaTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [businessAreas, setBusinessAreas] = useState(() => {
    const rawData = appService.getModulePreferences({ category: 'businessArea' });
    return rawData?.items || [];
  });

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const saveBusinessAreas = useCallback(
    async newItems => {
      setLoading(true);
      try {
        await appService.updateModulePreferences({
          category: 'businessArea',
          data: {
            items: newItems,
          },
        });
        setBusinessAreas(newItems);
        appService.message({ flag: 's', text: 'saved_successfully' });
      } catch (error) {
        console.error('[BusinessAreaTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const handleAdd = useCallback(() => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      label: '',
      foreColor: DEFAULT_FORE_COLOR,
      backColor: DEFAULT_BACK_COLOR,
    });
    setShowModal(true);
  }, [form]);

  const handleEdit = useCallback(
    item => {
      setEditingItem(item);
      form.setFieldsValue({
        label: item.label,
        foreColor: item.foreColor || DEFAULT_FORE_COLOR,
        backColor: item.backColor || DEFAULT_BACK_COLOR,
      });
      setShowModal(true);
    },
    [form]
  );

  const handleDelete = useCallback(
    async itemId => {
      const newItems = businessAreas.filter(item => item._id !== itemId);
      await saveBusinessAreas(newItems);
    },
    [businessAreas, saveBusinessAreas]
  );

  const handleModalOk = useCallback(async () => {
    try {
      const values = await form.validateFields();

      // Convert color objects to hex strings if needed
      const foreColor =
        typeof values.foreColor === 'object' ? values.foreColor.toHexString() : values.foreColor;
      const backColor =
        typeof values.backColor === 'object' ? values.backColor.toHexString() : values.backColor;

      let newItems;

      if (editingItem) {
        // Edit existing
        newItems = businessAreas.map(item =>
          item._id === editingItem._id
            ? { ...item, label: values.label, foreColor, backColor }
            : item
        );
      } else {
        // Add new
        const newItem = {
          _id: generateMongoObjectId(),
          label: values.label,
          foreColor,
          backColor,
        };
        newItems = [...businessAreas, newItem];
      }

      await saveBusinessAreas(newItems);
      setShowModal(false);
      form.resetFields();
    } catch (error) {
      console.error('[BusinessAreaTab] Form validation failed:', error);
    }
  }, [form, editingItem, businessAreas, saveBusinessAreas]);

  const handleModalCancel = useCallback(() => {
    setShowModal(false);
    form.resetFields();
  }, [form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        height: 'calc(80vh - 240px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: 8,
        paddingBottom: 16,
      }}
    >
      <Flex vertical gap={16}>
        <Card
          title={translateX('business_area')}
          size="small"
          extra={
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={loading}
            >
              {translateX('add')}
            </Button>
          }
        >
          {businessAreas.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={translateX('no_records_found')}
            />
          ) : (
            <Flex vertical gap={0}>
              {businessAreas.map((item, index) => (
                <Flex
                  key={item._id}
                  justify="space-between"
                  align="center"
                  style={{
                    padding: '10px 0',
                    borderBottom:
                      index < businessAreas.length - 1
                        ? '1px solid var(--ant-color-border-secondary)'
                        : 'none',
                  }}
                >
                  <Flex align="center" gap={8}>
                    <Badge
                      dot
                      style={{
                        backgroundColor: item.backColor || DEFAULT_BACK_COLOR,
                        width: 12,
                        height: 12,
                      }}
                    />
                    <Text style={{ fontSize: 13 }}>{item.label}</Text>
                  </Flex>

                  <Flex gap={4}>
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(item)}
                      disabled={loading}
                    />
                    <Popconfirm
                      title={translateX('confirm_delete')}
                      onConfirm={() => handleDelete(item._id)}
                      okText={translateX('yes')}
                      cancelText={translateX('no')}
                      disabled={loading}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        danger
                        disabled={loading}
                      />
                    </Popconfirm>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          )}
        </Card>

        <Text type="secondary" style={{ fontSize: 11, textAlign: 'center' }}>
          {translateX('business_area_info_description')}
        </Text>
      </Flex>

      {/* Add/Edit Modal */}
      <Modal
        title={
          <Flex align="center" gap={8}>
            <TagOutlined />
            {editingItem ? translateX('edit_business_area') : translateX('add_business_area')}
          </Flex>
        }
        open={showModal}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={translateX('save')}
        cancelText={translateX('cancel')}
        confirmLoading={loading}
        destroyOnHidden
        width={360}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="label"
            label={translateX('name')}
            rules={[{ required: true, message: translateX('field_required') }]}
          >
            <Input placeholder={translateX('enter_business_area_name')} autoFocus />
          </Form.Item>

          <Flex gap={16}>
            <Form.Item name="backColor" label={translateX('background_color')} style={{ flex: 1 }}>
              <ColorPicker format="hex" showText />
            </Form.Item>

            <Form.Item name="foreColor" label={translateX('text_color')} style={{ flex: 1 }}>
              <ColorPicker format="hex" showText />
            </Form.Item>
          </Flex>
        </Form>
      </Modal>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default BusinessAreaTab;
